// Application-wide constants. Domain-specific business rules live in
// server/ modules; this file is for stable identifiers used in many places.

export const APP_NAME = "The Solar Pro — Command Center";
export const APP_TAGLINE = "Track. Compete. Win.";
export const TIMEZONE = "America/Edmonton";

// VAPID identity for Web Push. PUBLIC is safe to ship to the browser — it's
// the "applicationServerKey" that the browser uses when subscribing. PRIVATE
// half is the Wrangler secret VAPID_PRIVATE_KEY on the Pages project and
// is never seen by the client. VAPID_SUBJECT is also a Pages secret (mailto:)
// — push services use it to contact the application owner if needed.
export const VAPID_PUBLIC_KEY =
  "BEZtOPWThZcWyqcC00iOiQOFmp9N8fyfg17yxL0ew_rYbvf4f4njuOcfhcT0WyNS2bD7aDY262_vnZUEDM9JHA8";

// GHL pipeline + stage IDs (Northern PWR location)
export const GHL_LOCATION_ID = "Kpb6f4H1MyY6lNUcxe4W";
export const GHL_API_VERSION = "2021-07-28";
export const GHL_BASE_URL = "https://services.leadconnectorhq.com";

// Workers/undici omit User-Agent by default. GHL sits behind Cloudflare;
// missing UA trips Browser Integrity Check (error 1010) and kills cron PITs.
// Stable product token — not a spoofed Chrome string.
export const GHL_USER_AGENT = "SolarCommand/1.0 (+https://cmd.thesolar.pro; GHL sync)";

export const GHL_PIPELINE_IDS = {
  // Live Northern PWR names from ghl_pipeline_stages (synced 2026-09-11):
  //   iPcVUhxBvlHsmD8gVsWQ = Appointment Pipeline
  //   8a9iwZSIGBTHq36dSEjU = Sales Pipeline
  //   ZGtAB24fpFon8RDxeyTL = Install Pipeline
  //   xfWDRFVsLnqVpGUMGUGh = Referral Pipeline
  // Historical keys `sales` / `sales2` keep their original IDs so existing
  // callers (lead-reassignment) still iterate both. STAGE ranking must use
  // appointment / live sales / install / referral — not these stale labels.
  appointment: "iPcVUhxBvlHsmD8gVsWQ",
  sales: "iPcVUhxBvlHsmD8gVsWQ",
  sales2: "8a9iwZSIGBTHq36dSEjU",
  install: "ZGtAB24fpFon8RDxeyTL",
  referral: "xfWDRFVsLnqVpGUMGUGh",
} as const;

/** Sales Pipeline > Deal Closed — Command recognition + STAGE display pivot. */
export const GHL_SALES_DEAL_CLOSED_STAGE_ID = "06c96596-bfa5-45a8-abc1-b7a07690d803";

// Hardcoded Ian GHL user id (the misrouted-leads target)
export const IAN_GHL_USER_ID = "jtOpozJdxnEtPOklyZRV";

// Workflow IDs that historically misrouted appointments (kept here for the
// reassignMisroutedLeads admin mutation's logging/audit context).
export const MISROUTING_WORKFLOWS = [
  "079e0434-62ab-4b0a-9ef4-6fb924216ab7", // "Call Disposition - Appointment Set"
  "85fbcc9e-b9a0-4eed-8b6b-83301865ee3e", // "New Sales Opportunity Created"
] as const;

// Presentation definition (Ian / FGE lock 2026-09-10): GHL calendar
// appointments with appointmentStatus "showed" whose start falls in the
// filter window. Team Pace uses the America/Edmonton ISO week (Mon 00:00 →
// next Mon 00:00). Leaderboard This Week / This Month / All Time use the
// same Showed + start-in-window rule on their respective windows. Not
// opportunity "booked", not Google Ads booking conversions, not pipeline
// stage "Presentation Completed".
export const TEAM_PACE_METRIC = "showed_presentations_per_week";
export const TEAM_PACE_DEFAULT_TARGET = 20;
export const TEAM_PACE_TARGET_CONFIG_KEY = "team_pace_target";
export const GHL_APPOINTMENT_SHOWED = "showed";

// Sync cadence
export const SYNC_INTERVAL_MIN = 10;
export const HEALTH_PROBE_MIN = 5;
export const STALE_SYNC_THRESHOLD_MIN = 25;

// Booking criteria slugs (the 6 "Paradise Awaits" criteria)
export const BOOKING_CRITERIA = [
  "appointment_booked",
  "presentation_completed",
  "contract_signed",
  "docusign_signed",
  "financing_stipulations_complete",
  "utility_bill_uploaded",
] as const;

export type BookingCriterion = (typeof BOOKING_CRITERIA)[number];

export const BOOKING_CRITERIA_LABELS: Record<BookingCriterion, string> = {
  appointment_booked: "Appointment Booked",
  presentation_completed: "Presentation Completed",
  contract_signed: "Contract Signed",
  docusign_signed: "DocuSign Signed",
  financing_stipulations_complete: "Financing Stipulations Complete",
  utility_bill_uploaded: "Utility Bill Uploaded",
};

// Buyer types for the SolarPitch presentations module
export const BUYER_TYPES = [
  { slug: "roi_eco", label: "ROI / Eco", emoji: "🌱" },
  { slug: "tech", label: "Tech-Forward", emoji: "🔬" },
  { slug: "skeptic", label: "Skeptic", emoji: "🧐" },
  { slug: "budget", label: "Budget-Conscious", emoji: "💰" },
  { slug: "premium", label: "Premium / Lifestyle", emoji: "✨" },
  { slug: "trust_seeker", label: "Trust-Seeker", emoji: "🤝" },
] as const;

// Mood chips for the momentum board
export const MOOD_CHIPS = [
  { slug: "on_fire", label: "On Fire", emoji: "🔥", color: "#ef4444" },
  { slug: "building", label: "Building", emoji: "🏗️", color: "#f97316" },
  { slug: "grinding", label: "Grinding", emoji: "💪", color: "#eab308" },
  { slug: "stuck", label: "Need Help", emoji: "🆘", color: "#3b82f6" },
  { slug: "comeback", label: "Comeback", emoji: "🎯", color: "#a855f7" },
  { slug: "fresh_start", label: "Fresh Start", emoji: "🌅", color: "#06b6d4" },
] as const;
